import React, { useEffect, useState, useMemo } from "react";
import { useLoader } from "../../Context/LoaderContext";
import teamMemberService from "../../Service/team-member.service";
import { openNotification } from "../../Components/Notification";
import {
  Button,
  Select,
  Skeleton,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Tooltip,
  Spin,
} from "antd";
import dayjs from "dayjs";

interface DataType {
  key: React.Key;
  id: string;
  createdBy: string;
  createdAt: string;
  inviteeEmail: string;
  status: string;
  expiresAt: string;
}

interface InviteListProps {
  handleInviteAgain: (email: string) => void;
  searchQuery?: string;
}
interface Invite {
  _id: string;
  inviteeEmail: string;
  orgId: string;
  verificationToken: string;
  createdBy: {
    _id: string;
    name: string;
  };
  status: "sent" | "accepted";
  expiresAt: Date;
  createdAt: Date;
  updatedAt: Date;
}

const InviteList: React.FC<InviteListProps> = ({
  handleInviteAgain,
  searchQuery = "",
}) => {
  const pageSize = 10;
  const { isLoading, setLoading } = useLoader();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);

  const fetchInvites = async (page: number, limit: number, status?: string) => {
    setLoading(true);
    const data = status
      ? await teamMemberService.getAllInvitesByStatus(status, page, limit)
      : await teamMemberService.getAllInvites(page, limit);
    setInvites(data?.invites || []);
    setTotalRecords(data?.totalInvites || 0);
    setLoading(false);
  };

  useEffect(() => {
    fetchInvites(page, pageSize, filterStatus ?? undefined);
  }, [page, filterStatus]);

  // Filter invites based on search query
  const filteredInvites = useMemo(() => {
    if (!searchQuery) return invites;
    return invites.filter(
      (invite) =>
        invite.inviteeEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invite.createdBy.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [invites, searchQuery]);

  const handleFilterByStatus = (status: string) => {
    setFilterStatus(status);
    setPage(1);
  };

  const handleClearFilter = () => {
    setFilterStatus(null);
    setPage(1);
  };

  const handleResend = async (inviteId: string) => {
    await teamMemberService.resendInvite(inviteId);
    openNotification("success", "Invitation mail sent successfully", "");
  };

  const handleDelete = async (inviteId: string) => {
    await teamMemberService.deleteInvite(inviteId);
    openNotification("success", "Invitation deleted successfully", "");
    fetchInvites(page, pageSize, filterStatus ?? undefined);
  };

  const columns: TableColumnsType<DataType> = [
    {
      title: "Invited On",
      dataIndex: "createdAt",
      key: "createdAt",
      minWidth: 120,
      render: (text) =>
        isLoading ? (
          <Skeleton.Input active size="small" />
        ) : (
          dayjs(text).format("DD-MMM-YY")
        ),
    },
    {
      title: "Email",
      dataIndex: "inviteeEmail",
      key: "inviteeEmail",
      minWidth: 200,
      render: (text) =>
        isLoading ? <Skeleton.Input active size="small" /> : text,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      minWidth: 100,
      render: (text, record) => {
        if (isLoading) return <Skeleton.Button active size="small" />;
        const isExpired = record.expiresAt
          ? dayjs().isAfter(dayjs(record.expiresAt))
          : false;
        const isSent = record.status === "sent" || record.status === "pending";
        return (
          <Space size="middle">
            {isSent && !isExpired && <Tag color="orange">Pending</Tag>}
            {isSent && isExpired && (
              <Tooltip
                title={`Expired on ${dayjs(record.expiresAt).format("DD-MMM-YY")}`}
              >
                <Tag color="red">Expired</Tag>
              </Tooltip>
            )}
            {record.status === "accepted" && <Tag color="green">Accepted</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      minWidth: 120,
      render: (text, record) => {
        if (isLoading) return <Skeleton.Button active size="small" />;
        if (record.status !== "sent") return null;
        const isExpired = dayjs().isAfter(dayjs(record.expiresAt));
        if (isExpired) {
          return (
            <Space size="small">
              <Button
                type="primary"
                size="small"
                onClick={() => handleInviteAgain(record.inviteeEmail)}
              >
                Invite Again
              </Button>
            </Space>
          );
        }
        return (
          <Space size="small">
            <Button size="small" onClick={() => handleResend(record.id)}>
              Resend Mail
            </Button>
            <Button danger size="small" onClick={() => handleDelete(record.id)}>
              Delete
            </Button>
          </Space>
        );
      },
    },
  ];

  // Calculate total min-width for horizontal scroll
  const totalMinWidth = columns.reduce((total, column) => {
    return total + (column.minWidth || 100);
  }, 0);

  const data: DataType[] = isLoading
    ? Array(4)
        .fill({})
        .map((_, index) => ({
          key: `skeleton-${index}`,
          id: "",
          createdBy: "",
          createdAt: "",
          inviteeEmail: "",
          status: "",
          expiresAt: "",
        }))
    : filteredInvites.map((invite) => ({
        key: invite._id,
        id: invite._id,
        createdBy: invite.createdBy.name,
        createdAt: dayjs(invite.createdAt).format("DD-MMM-YY"),
        inviteeEmail: invite.inviteeEmail,
        status: invite.status,
        expiresAt: invite.expiresAt ? String(invite.expiresAt) : "",
      }));

  const options = [
    { label: "Accepted", value: "accepted" },
    { label: "Sent", value: "sent" },
  ];

  return (
    <>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {isLoading ? (
          <Skeleton.Input size="small" active></Skeleton.Input>
        ) : (
          <>
            <Select
              value={filterStatus ?? "Filter By"}
              options={[{ label: "Filter By", value: "" }, ...options]}
              onChange={(value) => handleFilterByStatus(value)}
              size="middle"
              style={{ width: 120 }}
            />
            <Button
              style={{ marginLeft: "10px" }}
              className="secondary-button"
              onClick={handleClearFilter}
            >
              Clear Filter
            </Button>
          </>
        )}
      </div>

      <Spin spinning={isLoading}>
        <div
          className="table-container"
          style={{ width: "100%", overflow: "hidden" }}
        >
          <Table
            columns={columns}
            dataSource={!isLoading && data.length === 0 ? [] : data}
            locale={{
              emptyText: isLoading ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : (
                "No Data"
              ),
            }}
            size="small"
            scroll={{ x: totalMinWidth, y: 600 }}
            pagination={{
              current: page,
              total: totalRecords,
              pageSize: pageSize,
              showSizeChanger: false,
              hideOnSinglePage: true,
            }}
            onChange={(pagination) => setPage(pagination.current ?? 1)}
          />
        </div>
      </Spin>
    </>
  );
};

export default InviteList;
